import { z } from "zod";

export const GetZapParams = z.object({
  zap_id: z
    .string()
    .describe(
      "The unique identifier for the Zap you want to retrieve details for",
    ),
  account_id: z
    .string()
    .describe(
      "The account ID that owns the Zap. This is required to fetch the Zap.",
    ),
  // csrftoken is no longer needed as the proxy will use JWT
});

export type GetZapParamsType = z.infer<typeof GetZapParams>;

interface ZapStep {
  id: string | number;
  type: string;
  app: string;
  params: Record<string, unknown>;
}

interface ZapDraft {
  zdl: {
    steps: ZapStep[];
  };
}

export const getZapExecute = async (rawArgs: any) => {
  let params: GetZapParamsType;
  try {
    params = GetZapParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for getZap:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for get_zap. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const { zap_id, account_id } = params;

  const query = new URLSearchParams({
    zap_id,
    account_id,
  });
  const proxyApiUrl = `/api/canvasui/composer/getZap?${query.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for get_zap (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for get_zap (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for get_zap (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch (e) {
        errorDetailString = `Proxy error for get_zap (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[GET_ZAP_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[GET_ZAP_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[GET_ZAP_TOOL] Error in getZapExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error fetching Zap: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const getZapDescription =
  "Get detailed information about a specific Zap by ID. \nRequired parameters: \n- zap_id: The ID of the Zap to fetch. If the user mentions a Zap by name on the canvas, find its ID in 'additionalData.canvas.nodes' (look for a node with a matching label and use its 'data.zapId' or 'data.zap_id' property). \n- account_id: The ID of the account that owns the Zap. This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.";
